// "COMO QUER SER CHAMADO" + CANAIS DE CONTATO + "SEJA UM CORRETOR" no menu lateral
// (cs2656 Imovida, 01/10/2026 — gate em utils/topoContato: MENU_CONTATO_VISITANTE).
//
// O visitante informa o nome (fica guardado no navegador) e escolhe o canal: SMS, WhatsApp,
// ligação ou e-mail. A mensagem já sai assinada com o nome. Abaixo, o "Seja um corretor" abre um
// formulário curto que vira lead pelo relay /api/site-form (mesmo caminho das Páginas do Site).
import React, { useEffect, useState } from 'react';
import { FaChevronDown, FaEnvelope, FaPhoneAlt, FaSms, FaWhatsapp } from 'react-icons/fa';
import { ensureReadableTextColor } from '../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../utils/metaPixel';
import { resolveSiteWhatsAppUrl } from '../../utils/site2524WhatsApp';
import { buildContatoTopoUrl, buildSmsUrl, menuContatoVisitante } from '../../utils/topoContato';
import { trackLead } from '../whatsAppButton/WhatsAppButton';
import styles from './MenuContatoVisitante.module.css';

const CHAVE_NOME = 'visitante_nome';

const lerNome = (): string => {
    try {
        return (window.localStorage.getItem(CHAVE_NOME) || '').slice(0, 60);
    } catch {
        return '';
    }
};

const gravarNome = (nome: string) => {
    try {
        if (nome) window.localStorage.setItem(CHAVE_NOME, nome);
        else window.localStorage.removeItem(CHAVE_NOME);
    } catch {
        // navegação privada / storage bloqueado: o nome vale só enquanto o menu estiver aberto
    }
};

type Envio = 'parado' | 'enviando' | 'ok' | 'erro';

const MenuContatoVisitante: React.FC<{ site: any }> = ({ site }) => {
    const config = menuContatoVisitante(site);
    const [nome, setNome] = useState('');
    const [userAgent, setUserAgent] = useState('');
    const [corretorAberto, setCorretorAberto] = useState(false);
    const [envio, setEnvio] = useState<Envio>('parado');
    const [erros, setErros] = useState<Record<string, string>>({});

    useEffect(() => {
        setNome(lerNome());
        setUserAgent(window.navigator.userAgent || '');
    }, []);

    if (!config) return null;

    const fundo = site?.cor10 || '#ffffff';
    const corTexto = ensureReadableTextColor(site?.cor6, fundo);
    const corDestaque = ensureReadableTextColor(site?.cor2, fundo, 3, corTexto, corTexto);
    const vars = { ['--mcv-texto' as any]: corTexto, ['--mcv-destaque' as any]: corDestaque };

    const empresa = site?.Dados?.empresa ?? '';
    const tel = site?.Dados?.tel1 ?? '';
    const email = site?.Dados?.email1 || site?.Dados?.email2 || '';
    const nomeLimpo = nome.trim();
    const texto = `Olá${nomeLimpo ? `, sou ${nomeLimpo}` : ''}! Estou visitando o site da ${empresa} e gostaria de um atendimento.`;

    // ordem pedida pelo cliente: telefone, e-mail, SMS, WhatsApp
    const canais = [
        { id: 'phone', rotulo: 'Telefone', Icone: FaPhoneAlt, href: buildContatoTopoUrl({ telefone: tel, ehWhatsapp: false, texto }), nova: false },
        {
            id: 'email',
            rotulo: 'E-mail',
            Icone: FaEnvelope,
            href: email ? `mailto:${email}?subject=${encodeURIComponent(`Contato pelo site${nomeLimpo ? ` — ${nomeLimpo}` : ''}`)}&body=${encodeURIComponent(texto)}` : '',
            nova: false,
        },
        { id: 'sms', rotulo: 'SMS', Icone: FaSms, href: buildSmsUrl(tel, texto, userAgent), nova: false },
        {
            id: 'whatsapp',
            rotulo: 'WhatsApp',
            Icone: FaWhatsapp,
            href: buildContatoTopoUrl({ telefone: tel, ehWhatsapp: true, texto, resolveWhats: (f) => resolveSiteWhatsAppUrl(site, f) }),
            nova: true,
        },
    ].filter((c) => c.href);

    const enviarCorretor = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const dados = new FormData(e.currentTarget);
        const valor = (k: string) => String(dados.get(k) || '').trim();
        const novosErros: Record<string, string> = {};
        if (valor('nome').length < 2) novosErros.nome = 'Informe seu nome.';
        if (valor('telefone').replace(/\D/g, '').length < 10) novosErros.telefone = 'Informe um telefone com DDD.';
        if (valor('email') && !/^\S+@\S+\.\S+$/.test(valor('email'))) novosErros.email = 'E-mail inválido.';
        if (!dados.get('consent')) novosErros.consent = 'É preciso autorizar o contato.';
        setErros(novosErros);
        if (Object.keys(novosErros).length) return;

        setEnvio('enviando');
        const corpo = new URLSearchParams();
        dados.forEach((v, k) => corpo.append(k, String(v)));
        corpo.set('__page', String(config.paginaCorretor));
        corpo.set('__block', config.blocoCorretor);
        corpo.set('__origem', 'Seja um corretor');
        corpo.set('__labels', JSON.stringify({ nome: 'Nome completo', telefone: 'WhatsApp / Telefone', email: 'E-mail', creci: 'CRECI', mensagem: 'Mensagem' }));
        try {
            // o relay responde com redirect (303) de volta à página — aqui basta saber que aceitou.
            const res = await fetch('/api/site-form', {
                method: 'POST',
                headers: { 'content-type': 'application/x-www-form-urlencoded' },
                body: corpo.toString(),
                redirect: 'manual',
            });
            if (res.type === 'opaqueredirect' || res.ok) {
                gravarNome(valor('nome').slice(0, 60));
                setNome(valor('nome').slice(0, 60));
                setEnvio('ok');
                trackMetaEvent(site, 'Lead', { content_name: 'Seja um corretor', content_category: 'drawer' });
            } else {
                setEnvio('erro');
            }
        } catch {
            setEnvio('erro');
        }
    };

    return (
        <div className={styles.bloco} style={vars} data-menu-contato-visitante="">
            <label className={styles.rotulo} htmlFor="mcv-nome">
                Como quer ser chamado?
            </label>
            <input
                id="mcv-nome"
                className={styles.campo}
                type="text"
                autoComplete="given-name"
                maxLength={60}
                placeholder="Seu nome"
                value={nome}
                onChange={(ev) => {
                    setNome(ev.target.value);
                    gravarNome(ev.target.value.trim());
                }}
            />
            <div className={styles.rotulo}>{nomeLimpo ? `${nomeLimpo}, como você deseja ser contactado?` : 'Como você deseja ser contactado?'}</div>
            <div className={styles.canais}>
                {canais.map(({ id, rotulo, Icone, href, nova }) => (
                    <a
                        key={id}
                        href={href}
                        target={nova ? '_blank' : undefined}
                        rel={nova ? 'noopener noreferrer' : undefined}
                        className={styles.canal}
                        title={`Falar com a imobiliária por ${rotulo}`}
                        onClick={() => {
                            if (id === 'whatsapp') trackLead(site, null);
                            trackMetaEvent(site, 'Contact', { content_name: 'Contato do menu lateral', contact_method: id, content_category: 'drawer' });
                        }}
                    >
                        <span className={styles.canalIcone}>
                            <Icone aria-hidden="true" />
                        </span>
                        <span className={styles.canalRotulo}>{rotulo}</span>
                    </a>
                ))}
            </div>

            <button
                type="button"
                className={styles.corretorToggle}
                aria-expanded={corretorAberto}
                aria-controls="mcv-corretor"
                onClick={() => setCorretorAberto((v) => !v)}
            >
                Seja um corretor
                <FaChevronDown aria-hidden="true" className={corretorAberto ? styles.setaAberta : styles.seta} />
            </button>
            {corretorAberto ? (
                <div id="mcv-corretor">
                    {envio === 'ok' ? (
                        <p className={styles.sucesso} role="status">
                            Recebemos seu contato! Em breve falaremos com você.
                        </p>
                    ) : (
                        <form className={styles.form} onSubmit={enviarCorretor} noValidate>
                            {/* honeypot do relay: robô preenche, gente não vê */}
                            <input type="text" name="__hp" tabIndex={-1} autoComplete="off" className={styles.hp} aria-hidden="true" />
                            <label className={styles.rotuloCampo} htmlFor="mcv-c-nome">Nome completo</label>
                            <input id="mcv-c-nome" name="nome" className={styles.campo} defaultValue={nomeLimpo} autoComplete="name" maxLength={120} aria-invalid={!!erros.nome} />
                            {erros.nome ? <span className={styles.erro}>{erros.nome}</span> : null}
                            <label className={styles.rotuloCampo} htmlFor="mcv-c-tel">WhatsApp / Telefone</label>
                            <input id="mcv-c-tel" name="telefone" type="tel" className={styles.campo} autoComplete="tel" maxLength={20} placeholder="(11) 99999-9999" aria-invalid={!!erros.telefone} />
                            {erros.telefone ? <span className={styles.erro}>{erros.telefone}</span> : null}
                            <label className={styles.rotuloCampo} htmlFor="mcv-c-email">E-mail</label>
                            <input id="mcv-c-email" name="email" type="email" className={styles.campo} autoComplete="email" maxLength={120} aria-invalid={!!erros.email} />
                            {erros.email ? <span className={styles.erro}>{erros.email}</span> : null}
                            <label className={styles.rotuloCampo} htmlFor="mcv-c-creci">CRECI (se tiver)</label>
                            <input id="mcv-c-creci" name="creci" className={styles.campo} maxLength={30} />
                            <label className={styles.rotuloCampo} htmlFor="mcv-c-msg">Mensagem</label>
                            <textarea id="mcv-c-msg" name="mensagem" className={styles.campo} rows={3} maxLength={1000} />
                            <label className={styles.consent}>
                                <input type="checkbox" name="consent" value="sim" />
                                <span>Autorizo o contato e o tratamento dos meus dados conforme a Política de Privacidade.</span>
                            </label>
                            {erros.consent ? <span className={styles.erro}>{erros.consent}</span> : null}
                            {envio === 'erro' ? (
                                <span className={styles.erro} role="alert">
                                    Não foi possível enviar agora. Tente de novo em instantes.
                                </span>
                            ) : null}
                            <button type="submit" className={styles.enviar} disabled={envio === 'enviando'}>
                                {envio === 'enviando' ? 'Enviando…' : 'Enviar'}
                            </button>
                        </form>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default MenuContatoVisitante;
